import {
  captureWhatsAppEnquiryPayload,
  createWhatsAppClickEventId,
  createWhatsAppEnquiryRef,
  isWhatsAppEnquiryAttributionEnabled,
  postWhatsAppEnquiry,
  WHATSAPP_SEARCH_ENTRY_SOURCE,
  WHATSAPP_SEARCH_ENTRY_SURFACE,
} from './whatsappEnquiry.js';

// A Document owns one arrival, independent of React StrictMode effect remounts.
// Nothing is written to browser storage or carried into another document/tab.
const documentEntries = new WeakMap();

export function isWhatsAppSearchSitelinkLocation(location) {
  if (location?.pathname !== '/contact' || typeof location.search !== 'string'
    || location.search.length > 8192) return false;
  try {
    const values = new URLSearchParams(location.search).getAll('wa_entry');
    return values.length === 1 && values[0] === WHATSAPP_SEARCH_ENTRY_SOURCE;
  } catch (_) {
    return false;
  }
}

function freezePayload(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.values(value).forEach(freezePayload);
  return Object.freeze(value);
}

function topLevelDocument(browser) {
  try {
    if (!browser?.document || browser.top !== browser.self || browser.document.prerendering === true) return null;
    return browser.document;
  } catch (_) {
    return null;
  }
}

export function captureWhatsAppSearchSitelinkEntry({
  browser = typeof window !== 'undefined' ? window : null,
  enquiryEnabled = isWhatsAppEnquiryAttributionEnabled(),
  capturePayload = captureWhatsAppEnquiryPayload,
  postPayload = postWhatsAppEnquiry,
} = {}) {
  if (enquiryEnabled !== true) return null;
  const document = topLevelDocument(browser);
  if (!document) return null;
  if (documentEntries.has(document)) return documentEntries.get(document).payload;
  const state = { payload: null, reservation: null, consumed: false };
  documentEntries.set(document, state);
  if (!isWhatsAppSearchSitelinkLocation(browser.location)) return null;
  try {
    const enquiryRef = createWhatsAppEnquiryRef();
    if (!enquiryRef) return null;
    // Allocation is not an outgoing click. Google receives this ID only if the
    // first subsequent trusted WhatsApp activation actually completes dispatch.
    const payload = capturePayload({
      enquiryRef,
      clickEventId: createWhatsAppClickEventId(),
      surface: WHATSAPP_SEARCH_ENTRY_SURFACE,
      entrySource: WHATSAPP_SEARCH_ENTRY_SOURCE,
      pathname: '/contact',
      search: browser.location.search,
    });
    if (!payload) return null;
    state.payload = freezePayload(payload);
    postPayload(state.payload);
    return state.payload;
  } catch (_) {
    return state.payload;
  }
}

export function reserveWhatsAppSearchSitelinkEntry(options = {}) {
  const browser = options.browser ?? (typeof window !== 'undefined' ? window : null);
  const payload = captureWhatsAppSearchSitelinkEntry({ ...options, browser });
  const document = topLevelDocument(browser);
  const state = document ? documentEntries.get(document) : null;
  if (!payload || state?.consumed || state?.reservation) return null;
  const reservation = {};
  state.reservation = reservation;
  return {
    payload,
    finish(accepted) {
      if (state.reservation !== reservation) return;
      state.reservation = null;
      if (accepted === true) state.consumed = true;
    },
  };
}

export function canRetryWhatsAppSearchEntry(payload, currentConsent) {
  if (currentConsent?.ad_storage !== 'denied' && currentConsent?.ad_user_data !== 'denied') return true;
  const attribution = payload?.attribution;
  return !['gclid', 'gbraid', 'wbraid'].some((field) => Boolean(attribution?.[field]))
    && !attribution?.google_click_candidates?.some((candidate) => Boolean(candidate.identifier));
}
